'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import pushEvent, { EVENT_TYPE } from '@hub/common/src/analytics-utils';
import { useCookieConsentStatus } from '@hub/common-components/src/CookieConsentStatusProvider';
import { useGTMLoadedStatus } from './GTMLoadedStatusProvider';
import { pageKey } from './PostHogAnalyticsProvider';

// per document, not per instance: this remounts whenever the visitor returns to an account (A -> B -> A)
// or a list remounts with the same query, neither of which is a new session or a new search
const sessionStarted = new Set<string>();
let lastFormSubmit: string | null = null;

// used to track GA events for accounts that provide a GA measurement id
export default function GAAccountAnalyticsProvider({
  accountId,
  gaId
}: {
  accountId: string;
  gaId: string;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { hasConsent, consentLoaded } = useCookieConsentStatus();

  const { GTMLoaded } = useGTMLoadedStatus();

  const searchTerm = searchParams.get('query');

  useEffect(() => {
    // don't push any GA event before consent has been set to 'granted' or 'denied'
    // 'granted' == cookie tracking, 'denied' == cookie-less tracking
    // check if google tag manager tag has been loaded or pushEvent will no-op
    if (!consentLoaded || !GTMLoaded || !searchTerm) {
      return;
    }

    const key = `${accountId}|${searchTerm}`;
    if (key === lastFormSubmit) {
      return;
    }
    lastFormSubmit = key;

    pushEvent(EVENT_TYPE.FORM_SUBMIT, { accountId, gaId, search_term: searchTerm }, hasConsent);
  }, [accountId, gaId, searchTerm, consentLoaded, hasConsent, GTMLoaded]);

  // clearing ?query= on an entry page is not a new page view
  const page = pageKey(pathname, searchParams);

  useEffect(() => {
    if (!consentLoaded || !GTMLoaded) {
      return;
    }

    pushEvent(EVENT_TYPE.PAGE_VIEW, { accountId, gaId }, hasConsent);
  }, [accountId, gaId, page, consentLoaded, hasConsent, GTMLoaded]);

  useEffect(() => {
    if (!consentLoaded || !GTMLoaded || sessionStarted.has(accountId)) {
      return;
    }

    sessionStarted.add(accountId);
    pushEvent(EVENT_TYPE.SESSION_START, { accountId, gaId }, hasConsent);
  }, [accountId, gaId, consentLoaded, hasConsent, GTMLoaded]);

  return null;
}
